import { useState, useEffect } from 'react';
import OfferingPricingEditor from './commerce/OfferingPricingEditor';
import { backdropClose } from '../utils/backdropClose';
import SchedulingFields, { SchedulingValue, schedulingDefaults, schedulingBody } from './commerce/SchedulingFields';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import BranchSyncDialog from './BranchSyncDialog';
import NumberInput from './ui/NumberInput';

// Canonical bookable-type order for grouping plan access lists
const BOOKABLE_TYPE_GROUPS: Array<{ key: string; label: string }> = [
  { key: 'class', label: 'Classes' },
  { key: 'spa', label: 'Spa & Recovery' },
  { key: 'pt', label: 'Personal Training' },
  { key: 'consult', label: 'Consults' },
  { key: 'facility', label: 'Facilities' },
];

interface PlanManagementPanelProps {
  plan: {
    id: string;
    name: string;
    description: string | null;
    price: number | null;
    durationMonths: number | null;
    gstPercent?: number | null;
    isActive: boolean;
    features?: any;
    branchMappings?: Array<{
      branch: {
        id: string;
        name: string;
        displayName: string;
      };
    }>;
    _count: {
      memberships: number;
    };
  };
  onClose: () => void;
  onUpdate: () => void;
}

interface Membership {
  id: string;
  memberId: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
  member: {
    name: string;
    email: string;
  };
}

// TODO: Implement interfaces when API endpoints are ready
// interface PauseRequest {
//   id: string;
//   membershipId: string;
//   startDate: string;
//   endDate: string | null;
//   reason: string | null;
//   status: string;
//   membership: {
//     member: {
//       name: string;
//     };
//   };
// }

// interface GuestPass {
//   id: string;
//   guestName: string | null;
//   guestEmail: string | null;
//   status: string;
//   createdAt: string;
//   usedAt: string | null;
//   membership: {
//     member: {
//       name: string;
//     };
//   };
// }

// interface PlanAddOn {
//   id: string;
//   name: string;
//   description: string | null;
//   price: number;
//   isActive: boolean;
// }

interface PlanFeatures {
  planType?: 'standard' | 'pay_as_you_go' | 'entry_based' | 'hourly' | 'family' | 'group' | 'corporate' | 'seasonal' | 'goal_specific';
  pauseEnabled?: boolean;
  pauseRules?: {
    maxTotalDays?: number;
    requireReason?: boolean;
    /** Default true: a pause moves the end date by its length. */
    extendsValidity?: boolean;
  };
  /** Members may do these themselves in the app / web. */
  selfServiceEnabled?: { pause?: boolean; guestInvite?: boolean; upgrade?: boolean };
  externalGymAccess?: {
    enabled: boolean;
    days: number;
    gymIds?: string[];
  };
  maxUsers?: number;
  loyaltyPoints?: {
    enabled: boolean;
    pointsPerPurchase?: number;
    pointsPerVisit?: number;
  };
  entryBased?: {
    entryPrice?: number;
    bundleDiscounts?: Array<{ count: number; discount: number }>;
  };
  hourlyBased?: {
    hourlyRate?: number;
    minimumHours?: number;
  };
}

interface Branch {
  id: string;
  name: string;
  displayName: string;
  addressLine: string | null;
  city: string | null;
}

interface PlanFormData {
  name: string;
  description: string;
  durationMonths: string;
  price: string;
  gstPercent: string;
  isDefault: boolean;
  branchIds: string[];
}

export default function PlanManagementPanel({ plan, onClose, onUpdate }: PlanManagementPanelProps) {
  const [activeTab, setActiveTab] = useState<'edit' | 'features' | 'memberships' | 'pauses' | 'addons' | 'classAccess'>('edit');
  const [loading, setLoading] = useState(false);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [formData, setFormData] = useState<PlanFormData>({
    name: plan.name || '',
    description: plan.description || '',
    durationMonths: plan.durationMonths?.toString() || '',
    price: plan.price?.toString() || '',
    gstPercent: plan.gstPercent != null ? String(plan.gstPercent) : '18',
    isDefault: false,
    branchIds: []
  });
  const [formErrors, setFormErrors] = useState<Partial<PlanFormData>>({});
  const [features, setFeatures] = useState<PlanFeatures>(plan.features || {
    planType: 'standard',
    pauseEnabled: false,
    pauseRules: {
      maxPauses: 0,
      maxDaysPerPause: 0,
      maxTotalDays: 0,
      requireReason: false,
      extendExpiry: true,
    },
    externalGymAccess: {
      enabled: false,
      days: 0,
    },
    loyaltyPoints: {
      enabled: false,
      pointsPerPurchase: 0,
      pointsPerVisit: 0,
    },
  });

  // Data states
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [pauseRequests, setPauseRequests] = useState<any[]>([]);
  const [showBranchSyncDialog, setShowBranchSyncDialog] = useState(false);
  const [originalBranchIds, setOriginalBranchIds] = useState<string[]>([]);

  // Class access state
  const [classAccessList, setClassAccessList] = useState<any[]>([]);
  const [availableClasses, setAvailableClasses] = useState<any[]>([]);
  const [addingClass, setAddingClass] = useState(false);
  const [newClassId, setNewClassId] = useState('');
  const [newClassSessions, setNewClassSessions] = useState('');
  const [newClassPeriod, setNewClassPeriod] = useState<'month' | 'week'>('month');
  // Who delivers it: anyone free, a coach the member picks at purchase, or one the gym assigns.
  const [newTrainerMode, setNewTrainerMode] = useState<'any' | 'member_picks' | 'gym_assigns'>('any');
  const [newTrainerLocked, setNewTrainerLocked] = useState(false);
  const [newSched, setNewSched] = useState<SchedulingValue>(schedulingDefaults());
  const [savingClassAccess, setSavingClassAccess] = useState(false);


  const fetchData = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const apiUrl = API_BASE_URL;
      const headers = {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      };

      // Fetch data based on active tab
      if (activeTab === 'memberships') {
        const membershipsRes = await fetch(`${apiUrl}/api/member-plans/${plan.id}/memberships`, { headers });
        if (membershipsRes.ok) {
          const membershipsData = await membershipsRes.json();
          setMemberships(membershipsData.data || []);
        }
      } else if (activeTab === 'pauses') {
        const pausesRes = await fetch(`${apiUrl}/api/membership-management/pauses/plan/${plan.id}`, { headers });
        if (pausesRes.ok) {
          const pausesData = await pausesRes.json();
          setPauseRequests(pausesData.data || []);
        }
      } else if (activeTab === 'classAccess') {
        const [accessRes, classesRes] = await Promise.all([
          fetch(`${apiUrl}/api/member-plans/${plan.id}/class-access`, { headers }),
          fetch(`${apiUrl}/api/classes?includeInactive=false`, { headers }),
        ]);
        if (accessRes.ok) setClassAccessList((await accessRes.json()).data || []);
        if (classesRes.ok) setAvailableClasses((await classesRes.json()).data || []);
      }
    } catch (error) {
      console.error('Error fetching plan data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    // Add overflow-hidden to body when modal opens
    document.body.style.overflow = 'hidden';

    // Cleanup: restore body scroll when modal closes
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    if (plan.features) {
      setFeatures(plan.features);
    }
  }, [plan.features]);

  // Fetch branches when edit tab is active
  useEffect(() => {
    if (activeTab === 'edit') {
      fetchBranches();
      // Load plan branch mappings
      if (plan.branchMappings) {
        const branchIds = plan.branchMappings?.map((mapping: any) => mapping.branch.id) || [];
        setFormData(prev => ({
          ...prev,
          branchIds
        }));
        setOriginalBranchIds(branchIds); // Track original for change detection
      }
    }
  }, [activeTab]);

  const fetchBranches = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const apiUrl = API_BASE_URL;
      const branchesResponse = await fetch(`${apiUrl}/api/branches`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (branchesResponse.ok) {
        const branchesData = await branchesResponse.json();
        setBranches(branchesData.data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  useEffect(() => {
    if (activeTab !== 'features') {
      fetchData();
    }
  }, [activeTab, plan.id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (formErrors[name as keyof PlanFormData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleBranchToggle = (branchId: string) => {
    setFormData(prev => ({
      ...prev,
      branchIds: prev.branchIds.includes(branchId)
        ? prev.branchIds.filter(id => id !== branchId)
        : [...prev.branchIds, branchId]
    }));
  };

  const validatePlanForm = (): boolean => {
    const errors: Partial<PlanFormData> = {};

    if (!formData.name.trim()) {
      errors.name = 'Plan name is required';
    }

    if (!formData.price) {
      errors.price = 'Price is required';
    } else if (isNaN(Number(formData.price))) {
      errors.price = 'Price must be a valid number';
    }

    if (!formData.durationMonths) {
      errors.durationMonths = 'Duration is required';
    } else if (isNaN(Number(formData.durationMonths)) || Number(formData.durationMonths) < 1) {
      errors.durationMonths = 'Duration must be a positive number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTogglePlanStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;

      const response = await fetch(`${apiUrl}/api/member-plans/${plan.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          isActive: !plan.isActive
        })
      });

      if (response.ok) {
        toast.success(`Plan ${!plan.isActive ? 'activated' : 'deactivated'} successfully!`);
        onUpdate();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to update plan status');
      }
    } catch (error) {
      console.error('Error updating plan status:', error);
      toast.error('Failed to update plan status');
    }
  };

  const handleSavePlan = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePlanForm()) {
      return;
    }

    // Check if branches changed
    const branchesChanged = JSON.stringify([...formData.branchIds].sort()) !== JSON.stringify([...originalBranchIds].sort());

    setSavingPlan(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;

      const payload: any = {
        name: formData.name,
        description: formData.description,
        durationMonths: formData.durationMonths ? Number(formData.durationMonths) : null,
        price: formData.price ? Number(formData.price) : null,
        gstPercent: formData.gstPercent === '' ? 0 : Number(formData.gstPercent),
        isDefault: formData.isDefault,
        branchIds: formData.branchIds
      };

      const response = await fetch(`${apiUrl}/api/member-plans/${plan.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Plan updated successfully!');

        // Show info toast about branch changes
        if (branchesChanged && plan._count.memberships > 0) {
          toast(
            'Branch changes will apply to new memberships only.\nYou can sync existing memberships if needed.',
            { duration: 4000, icon: 'ℹ️' }
          );
          // Show sync dialog after a brief delay
          setTimeout(() => {
            setShowBranchSyncDialog(true);
          }, 500);
        }

        onUpdate();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to update plan');
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      toast.error('Failed to update plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const handleFeatureChange = (path: string, value: any) => {
    setFeatures(prev => {
      const newFeatures = { ...prev };
      const keys = path.split('.');
      let current: any = newFeatures;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newFeatures;
    });
  };

  const cleanFeatures = (features: PlanFeatures): PlanFeatures => {
    const cleaned: any = { ...features };

    // Clean up pauseRules
    if (cleaned.pauseRules) {
      // Remove deprecated fields
      delete (cleaned.pauseRules as any).extendExpiry;
      delete (cleaned.pauseRules as any).maxPauses;
      delete (cleaned.pauseRules as any).maxDaysPerPause;
    }

    // Remove pauseRules if pauseEnabled is false
    if (!cleaned.pauseEnabled && cleaned.pauseRules) {
      delete cleaned.pauseRules;
    }

    // Remove guestPasses if enabled is false
    if (cleaned.guestPasses && !cleaned.guestPasses.enabled) {
      delete cleaned.guestPasses;
    }

    // Remove externalGymAccess if enabled is false
    if (cleaned.externalGymAccess && !cleaned.externalGymAccess.enabled) {
      delete cleaned.externalGymAccess;
    }

    // Remove loyaltyPoints if enabled is false
    if (cleaned.loyaltyPoints && !cleaned.loyaltyPoints.enabled) {
      delete cleaned.loyaltyPoints;
    }

    // Remove entryBased if planType is not entry_based or pay_as_you_go
    if (cleaned.planType !== 'entry_based' && cleaned.planType !== 'pay_as_you_go' && cleaned.entryBased) {
      delete cleaned.entryBased;
    }

    // Remove hourlyBased if planType is not hourly
    if (cleaned.planType !== 'hourly' && cleaned.hourlyBased) {
      delete cleaned.hourlyBased;
    }

    // Remove maxUsers if planType is not family/group/corporate
    if (!['family', 'group', 'corporate'].includes(cleaned.planType || 'standard') && cleaned.maxUsers) {
      delete cleaned.maxUsers;
    }

    // Clean up selfServiceEnabled - remove if all are false
    if (cleaned.selfServiceEnabled) {
      const hasAnyEnabled = cleaned.selfServiceEnabled.pause ||
        cleaned.selfServiceEnabled.guestInvite ||
        cleaned.selfServiceEnabled.upgrade;
      if (!hasAnyEnabled) {
        delete cleaned.selfServiceEnabled;
      }
    }

    // Clean up timeRestrictions - remove if offPeakOnly is false and no allowedHours
    if (cleaned.timeRestrictions) {
      const hasRestrictions = cleaned.timeRestrictions.offPeakOnly ||
        (cleaned.timeRestrictions.allowedHours && cleaned.timeRestrictions.allowedHours.length > 0);
      if (!hasRestrictions) {
        delete cleaned.timeRestrictions;
      }
    }

    // Clean up dynamicPricing - remove if no discounts set
    if (cleaned.dynamicPricing) {
      const hasPricing = cleaned.dynamicPricing.offPeakDiscount ||
        cleaned.dynamicPricing.commitmentDiscount ||
        (cleaned.dynamicPricing.seasonalMultipliers && Object.keys(cleaned.dynamicPricing.seasonalMultipliers).length > 0);
      if (!hasPricing) {
        delete cleaned.dynamicPricing;
      }
    }

    return cleaned;
  };

  const handleSaveFeatures = async () => {
    setSavingFeatures(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      // Clean up features before sending
      const cleanedFeatures = cleanFeatures(features);

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/plan-features/${plan.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanedFeatures)
      });

      if (response.ok) {
        toast.success('Plan features updated successfully!');
        onUpdate();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to update plan features');
      }
    } catch (error) {
      console.error('Error updating plan features:', error);
      toast.error('Failed to update plan features');
    } finally {
      setSavingFeatures(false);
    }
  };


  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-[1000] p-0 sm:p-4" {...backdropClose(onClose)}>
        <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90dvh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex-shrink-0">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-2xl font-bold text-gray-900">{plan.name}</h2>
                  <button
                    onClick={handleTogglePlanStatus}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${plan.isActive
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    title={plan.isActive ? 'Deactivate Plan' : 'Activate Plan'}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={plan.isActive ? "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"} />
                    </svg>
                    <span>{plan.isActive ? 'Deactivate' : 'Activate'}</span>
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-1">{plan.description || 'No description'}</p>
                <div className="flex items-center space-x-4 mt-3">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">₹{plan.price || 'N/A'}</span>
                  </div>
                  {plan.durationMonths && (
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm text-gray-700">{plan.durationMonths} months</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="text-sm text-gray-700">{plan._count.memberships} active memberships</span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors ml-4"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 flex-shrink-0 px-6">
            <div className="flex space-x-1 overflow-x-auto">
              {[
                { id: 'edit', label: 'Edit Plan', icon: '✏️' },
                { id: 'features', label: 'Features', icon: '⚙️' },
                { id: 'memberships', label: 'Memberships', icon: '👥' },
                { id: 'pauses', label: 'Pauses', icon: '⏸️' },
                { id: 'classAccess', label: 'Bookings access', icon: '🏃' },
                { id: 'addons', label: 'Add-ons', icon: '➕', comingSoon: true },
              ].map((tab: any) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                  {tab.comingSoon && (
                    <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-[10px] uppercase font-bold tracking-wider rounded-full">
                      Soon
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {activeTab === 'edit' && (
                  <div className="p-6 space-y-6">
                    <form onSubmit={handleSavePlan} className="space-y-6">
                      {/* Basic Information */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                          Basic Information
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Plan Name *
                            </label>
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.name ? 'border-red-500' : 'border-gray-300'
                                }`}
                              placeholder="Enter plan name"
                            />
                            {formErrors.name && (
                              <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Price (₹) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              name="price"
                              value={formData.price}
                              onChange={handleInputChange}
                              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.price ? 'border-red-500' : 'border-gray-300'
                                }`}
                              placeholder="Enter price"
                              min="0"
                              step="0.01"
                            />
                            {formErrors.price && (
                              <p className="text-red-500 text-sm mt-1">{formErrors.price}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              GST %
                            </label>
                            <input
                              type="number"
                              name="gstPercent"
                              value={formData.gstPercent}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="18"
                              min="0"
                              max="100"
                              step="0.01"
                            />
                            <p className="text-xs text-gray-500 mt-1">Tax rate when a registered branch sells this plan. Use 0 for exempt.</p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Duration (Months) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              name="durationMonths"
                              value={formData.durationMonths}
                              onChange={handleInputChange}
                              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.durationMonths ? 'border-red-500' : 'border-gray-300'
                                }`}
                              placeholder="Enter duration"
                              min="1"
                            />
                            {formErrors.durationMonths && (
                              <p className="text-red-500 text-sm mt-1">{formErrors.durationMonths}</p>
                            )}
                          </div>

                          <div className="flex items-center">
                            <label className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                name="isDefault"
                                checked={formData.isDefault}
                                onChange={handleInputChange}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">
                                Set as default plan
                              </span>
                            </label>
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Description
                            </label>
                            <textarea
                              name="description"
                              value={formData.description}
                              onChange={handleInputChange}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Enter plan description"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Branch Assignment */}
                      {branches.length > 0 && (
                        <div className="space-y-4">
                          <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                            Branch Assignment
                          </h3>

                          <div>
                            <p className="text-sm text-gray-600 mb-3">
                              Select which branches this plan will be available at. Leave empty to make it available at all branches.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {branches.map(branch => (
                                <label key={branch.id} className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={formData.branchIds.includes(branch.id)}
                                    onChange={() => handleBranchToggle(branch.id)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="text-sm text-gray-700">
                                    {branch.displayName || branch.name}
                                    {branch.city && ` (${branch.city})`}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Form Actions */}
                      <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={onClose}
                          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={savingPlan}
                          className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                          {savingPlan && (
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          )}
                          <span>{savingPlan ? 'Updating...' : 'Update Plan'}</span>
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {activeTab === 'features' && (
                  <div className="pb-8">
                    <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                      <h3 className="text-lg font-semibold text-gray-900">Plan Features Configuration</h3>
                      <button
                        onClick={handleSaveFeatures}
                        disabled={savingFeatures}
                        className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {savingFeatures && (
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        )}
                        <span>{savingFeatures ? 'Saving...' : 'Save Changes'}</span>
                      </button>
                    </div>

                    <div className="p-6 space-y-6">


                      {/* Pause Settings */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                          Pause Settings
                        </h3>
                        <div className="space-y-3">
                          <label className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={features.pauseEnabled || false}
                              onChange={(e) => handleFeatureChange('pauseEnabled', e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Enable pause feature</span>
                          </label>

                          {features.pauseEnabled && (
                            <div className="ml-6 space-y-3 p-4 bg-gray-50 rounded-lg">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Max Total Days
                                  </label>
                                  <NumberInput
                                    integer
                                    min={0}
                                    emptyValue={0}
                                    value={features.pauseRules?.maxTotalDays || 0}
                                    onChange={(v) => handleFeatureChange('pauseRules.maxTotalDays', v ?? 0)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                                <div className="flex items-center pt-6">
                                  <label className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      checked={features.pauseRules?.requireReason || false}
                                      onChange={(e) => handleFeatureChange('pauseRules.requireReason', e.target.checked)}
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">Require reason</span>
                                  </label>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500">0 = no limit on total pause days.</p>
                              <label className="flex items-start space-x-2">
                                <input
                                  type="checkbox"
                                  checked={features.pauseRules?.extendsValidity !== false}
                                  onChange={(e) => handleFeatureChange('pauseRules.extendsValidity', e.target.checked)}
                                  className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">
                                  A pause moves the end date
                                  <span className="block text-xs text-gray-500">Off: the member keeps the same end date and simply isn't charged sessions while paused.</span>
                                </span>
                              </label>
                              <label className="flex items-start space-x-2">
                                <input
                                  type="checkbox"
                                  checked={features.selfServiceEnabled?.pause === true}
                                  onChange={(e) => handleFeatureChange('selfServiceEnabled.pause', e.target.checked)}
                                  className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">
                                  Members can pause it themselves
                                  <span className="block text-xs text-gray-500">In the Gympe app and member web, within these rules. Approved at once.</span>
                                </span>
                              </label>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Other Features - Coming Soon */}
                      <div className="space-y-4 opacity-50 relative pointer-events-none">
                        <div className="absolute inset-0 flex items-center justify-center z-10">
                          <span className="bg-yellow-100 text-yellow-800 text-sm font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-sm">
                            Coming Soon
                          </span>
                        </div>

                        {/* Loyalty & Rewards Placeholder */}
                        <div className="space-y-4 filter blur-[1px]">
                          <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                            Loyalty & Rewards
                          </h3>
                          <div className="space-y-3">
                            <label className="flex items-center space-x-3">
                              <input type="checkbox" disabled className="rounded border-gray-300" />
                              <span className="text-sm text-gray-700">Enable loyalty points</span>
                            </label>
                          </div>
                        </div>

                        {/* Advanced Plan Types Placeholder */}
                        <div className="space-y-4 filter blur-[1px]">
                          <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                            Advanced Settings
                          </h3>
                          <p className="text-sm text-gray-500">
                            Family plans, corporate memberships, and entry-based pricing configurations will be available soon.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'memberships' && (
                  <div className="p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Active Memberships</h3>
                    {memberships.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <p>No active memberships found for this plan</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {memberships.map((membership) => (
                          <div key={membership.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900">{membership.member?.name || 'Unknown Member'}</p>
                                <p className="text-sm text-gray-600">{membership.member?.email || ''}</p>
                                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                                  {membership.startDate && (
                                    <span>Start: {new Date(membership.startDate).toLocaleDateString()}</span>
                                  )}
                                  {membership.endDate && (
                                    <span>End: {new Date(membership.endDate).toLocaleDateString()}</span>
                                  )}
                                </div>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${membership.status === 'active' ? 'bg-green-100 text-green-800' :
                                membership.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                {membership.status || 'active'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'pauses' && (
                  <div className="p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Pause Requests</h3>
                    {pauseRequests.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <p>No pause requests found for this plan</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {pauseRequests.map((pause) => (
                          <div key={pause.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{pause.membership?.member?.fullName || 'Unknown Member'}</p>
                                <p className="text-sm text-gray-600">{pause.membership?.member?.email || ''}</p>
                                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                                  <span>Start: {new Date(pause.startDate).toLocaleDateString()}</span>
                                  {pause.endDate && (
                                    <span>End: {new Date(pause.endDate).toLocaleDateString()}</span>
                                  )}
                                  {pause.reason && (
                                    <span className="text-gray-400">• {pause.reason}</span>
                                  )}
                                </div>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${pause.status === 'active' ? 'bg-yellow-100 text-yellow-800' :
                                pause.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                  pause.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}>
                                {pause.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'classAccess' && (
                  <div className="p-6 space-y-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">Bookings Access</h3>
                        <p className="text-sm text-gray-500 mt-0.5">Classes, spa, PT, consults and courts included in this membership plan — e.g. "4 PT sessions/month + unlimited classes"</p>
                      </div>
                      <button
                        onClick={() => { setAddingClass(true); setNewClassId(''); setNewClassSessions(''); setNewClassPeriod('month'); setNewTrainerMode('any'); setNewTrainerLocked(false); }}
                        className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <span>+</span> Add Bookable
                      </button>
                    </div>

                    {addingClass && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                        <p className="text-xs font-bold text-blue-700 uppercase tracking-wider">Add Bookable to Plan</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Bookable</label>
                            <select
                              value={newClassId}
                              onChange={e => setNewClassId(e.target.value)}
                              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
                            >
                              <option value="">— Select —</option>
                              {BOOKABLE_TYPE_GROUPS.map(({ key, label }) => {
                                const options = availableClasses
                                  .filter(c => (c.bookableType || 'class') === key)
                                  .filter(c => !classAccessList.some((a: any) => a.classId === c.id));
                                if (options.length === 0) return null;
                                return (
                                  <optgroup key={key} label={label}>
                                    {options.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                  </optgroup>
                                );
                              })}
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Sessions (blank = unlimited)</label>
                            <div className="flex gap-2">
                              <input
                                type="number"
                                min={1}
                                value={newClassSessions}
                                onChange={e => setNewClassSessions(e.target.value)}
                                placeholder="Unlimited"
                                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                              />
                              <select value={newClassPeriod} onChange={e => setNewClassPeriod(e.target.value as any)} disabled={!newClassSessions} className="px-2 py-2 rounded-lg border border-gray-200 text-sm bg-white disabled:opacity-50">
                                <option value="month">/ month</option>
                                <option value="week">/ week</option>
                              </select>
                            </div>
                          </div>
                          <div className="col-span-2">
                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">Who delivers it</label>
                            <select value={newTrainerMode} onChange={e => setNewTrainerMode(e.target.value as any)} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white">
                              <option value="any">Anyone free</option>
                              <option value="member_picks">Member picks a coach when they buy</option>
                              <option value="gym_assigns">Gym assigns a coach</option>
                            </select>
                            {newTrainerMode !== 'any' && (
                              <label className="flex items-center gap-2 mt-2 text-xs font-bold text-gray-700">
                                <input type="checkbox" checked={newTrainerLocked} onChange={e => setNewTrainerLocked(e.target.checked)} className="accent-blue-600" />
                                Book only with that coach
                              </label>
                            )}
                          </div>
                          {(availableClasses.find((c: any) => c.id === newClassId)?.scheduleMode ?? 'appointments') === 'appointments' && (
                            <SchedulingFields value={newSched} trainerMode={newTrainerMode} onChange={setNewSched} />
                          )}
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button
                            disabled={!newClassId || savingClassAccess}
                            onClick={async () => {
                              if (!newClassId) return;
                              setSavingClassAccess(true);
                              try {
                                const { data: { session } } = await supabase.auth.getSession();
                                if (!session) return;
                                const res = await fetch(`${API_BASE_URL}/api/member-plans/${plan.id}/class-access/${newClassId}`, {
                                  method: 'PUT',
                                  headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    sessions: newClassSessions ? parseInt(newClassSessions) : null,
                                    period: newClassPeriod,
                                    trainerMode: newTrainerMode,
                                    trainerLocked: newTrainerMode !== 'any' && newTrainerLocked,
                                    ...schedulingBody(newTrainerMode === 'any' ? { ...newSched, scheduling: 'on_demand' } : newSched),
                                  })
                                });
                                const d = await res.json();
                                if (!d.success) throw new Error(d.error || 'Failed');
                                setClassAccessList(prev => [...prev, d.data]);
                                setAddingClass(false);
                              } catch (err: any) {
                                alert(err.message || 'Failed to add class');
                              } finally {
                                setSavingClassAccess(false);
                              }
                            }}
                            className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg disabled:opacity-50 hover:bg-blue-700 transition-colors"
                          >
                            {savingClassAccess ? 'Saving…' : 'Add'}
                          </button>
                          <button onClick={() => setAddingClass(false)} className="px-4 py-2 text-xs font-bold text-gray-500 hover:text-gray-700 rounded-lg">
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {loading ? (
                      <div className="py-10 text-center text-gray-400">Loading…</div>
                    ) : classAccessList.length === 0 ? (
                      <div className="py-12 flex flex-col items-center text-center">
                        <div className="text-4xl mb-3">🏃</div>
                        <p className="font-bold text-gray-400">No bookings included</p>
                        <p className="text-sm text-gray-300 mt-1">Members on this plan will be charged per session unless you add bookables here.</p>
                      </div>
                    ) : (
                      <div className="space-y-5">
                        {BOOKABLE_TYPE_GROUPS.map(({ key, label }) => {
                          const group = classAccessList.filter((a: any) => (a.class?.bookableType || 'class') === key);
                          if (group.length === 0) return null;
                          return (
                            <div key={key}>
                              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5 px-1">{label}</p>
                              <div className="space-y-2">
                                {group.map((a: any) => (
                                  <div key={a.id || a.classId} className="flex items-center justify-between bg-white border border-gray-100 rounded-xl px-4 py-3">
                                    <div>
                                      <p className="font-bold text-sm text-gray-900">{a.class?.name}</p>
                                      <p className="text-xs text-gray-400">{a.class?.category || 'Uncategorised'}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      {a.trainerMode && a.trainerMode !== 'any' && (
                                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-lime-50 text-lime-800 border border-lime-200">
                                          {a.trainerMode === 'member_picks' ? 'Member picks coach' : 'Gym assigns coach'}{a.trainerLocked ? ' · only them' : ''}
                                        </span>
                                      )}
                                      {a.scheduling && a.scheduling !== 'on_demand' && (
                                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                                          {a.scheduling === 'fixed_recurring' ? 'Fixed weekly' : 'Weekly or on demand'}{a.slotsPerWeek ? ` · ${a.slotsPerWeek}×/wk` : ''}
                                        </span>
                                      )}
                                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                                        {a.sessions != null ? `${a.sessions}/${a.period === 'week' ? 'wk' : 'mo'}` : a.sessionsPerMonth != null ? `${a.sessionsPerMonth}/mo` : 'Unlimited'}
                                      </span>
                                      <button
                                        onClick={async () => {
                                          if (!confirm('Remove this bookable from the plan?')) return;
                                          try {
                                            const { data: { session } } = await supabase.auth.getSession();
                                            if (!session) return;
                                            await fetch(`${API_BASE_URL}/api/member-plans/${plan.id}/class-access/${a.classId}`, {
                                              method: 'DELETE',
                                              headers: { Authorization: `Bearer ${session.access_token}` }
                                            });
                                            setClassAccessList(prev => prev.filter((x: any) => x.classId !== a.classId));
                                          } catch { alert('Failed to remove'); }
                                        }}
                                        className="text-xs text-red-400 hover:text-red-600 font-bold"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'classAccess' && classAccessList.some((a: any) => a.trainerMode && a.trainerMode !== 'any') && (
                  <div className="px-6 pb-6">
                    <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4 space-y-2">
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Plan price by coach level</p>
                      <OfferingPricingEditor ownerType="plan" ownerId={plan.id} />
                    </div>
                  </div>
                )}

                {activeTab === 'addons' && (
                  <div className="flex flex-col items-center justify-center py-16 px-4">
                    <div className="bg-blue-50 p-4 rounded-full mb-6">
                      <span className="text-4xl">➕</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Add-ons Coming Soon</h3>
                    <p className="text-gray-500 text-center max-w-md mb-8">
                      We're working on a powerful way for you to offer additional services like personal training, diet plans, and merchandise directly through your plans.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg">
                        <span>🏋️</span> Personal Training
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg">
                        <span>🥗</span> Diet Plans
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg">
                        <span>🧖‍♀️</span> Spa Services
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

      </div>

      <BranchSyncDialog
        isOpen={showBranchSyncDialog}
        planId={plan.id}
        planName={plan.name}
        activeMembershipsCount={plan._count.memberships}
        onClose={() => setShowBranchSyncDialog(false)}
        onSuccess={() => {
          onUpdate();
          setShowBranchSyncDialog(false);
        }}
      />
    </>
  );
}
